import OSS from 'ali-oss/lib/client'
import request from "app/vendor/request"
import config from "app/config"

/**
 * 使用ali-oss SDK上传文件
 * 支持断点续传、进度跟踪、自动分片、STS Token自动刷新
 */
export class OssUploader {
  constructor() {
    this.client = null
    this.stsData = null
  }

  /**
   * 获取STS临时凭证
   * 支持重复调用，用于初始化和刷新Token
   */
  async getStsToken() {
    try {
      console.log('🔄 获取或刷新 STS Token...')
      const response = await request.get(`${config.backend}/sts/token`)
        .checkToken()
        .commonDispose()
      
      this.stsData = response
      console.log('✅ STS Token 获取成功')
      return response
    } catch (error) {
      console.error('❌ 获取 STS Token 失败:', error)
      throw new Error('获取上传凭证失败')
    }
  }

  /**
   * 初始化OSS客户端
   * 支持STS Token自动刷新，确保大文件长时间上传成功
   */
  async initClient() {
    // 如果客户端已存在，则直接返回，避免重复初始化
    if (this.client) {
      return this.client
    }

    // 首次初始化时，必须先获取一次 STS Token
    if (!this.stsData) {
      await this.getStsToken()
    }

    console.log('🔧 初始化OSS客户端...')
    this.client = new OSS({
      accessKeyId: this.stsData.accessKeyId,
      accessKeySecret: this.stsData.accessKeySecret,
      stsToken: this.stsData.stsToken,
      bucket: this.stsData.bucket,
      region: this.stsData.region,
      endpoint: this.stsData.endpoint,
      secure: true, // 强制使用HTTPS
      timeout: 1200000, // 20分钟超时（针对单个分片请求）
      retryMax: 3, // 单个请求的最大重试次数
      
      // STS Token 自动刷新配置
      refreshSTSToken: async () => {
        await this.getStsToken() // 调用我们自己的方法来刷新凭证
        return {
          accessKeyId: this.stsData.accessKeyId,
          accessKeySecret: this.stsData.accessKeySecret,
          stsToken: this.stsData.stsToken,
        }
      },
      // 提前5分钟刷新，确保 Token 永远有效
      // 如果 Token 有效期为1小时(3600秒)，设置为 (3600 - 300) * 1000 = 55分钟
      // 这里保守地设置为每30分钟检查一次
      refreshSTSTokenInterval: 1800000 
    })

    console.log('✅ OSS客户端初始化成功，并已配置 STS Token 自动刷新')
    return this.client
  }

  /**
   * 生成文件对象名
   * @param {File} file - 文件对象
   * @param {string} [appType=''] - 应用类型，用于生成对应的目录
   * @returns {string} - 完整的 Object Key，例如 "pgs/1678886400000-xyz.bam"
   */
  generateObjectName(file, appType = '') {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 12)
    const ext = file.name.split('.').pop()
    
    // 根据应用类型确定上传目录
    let uploadDir = ''
    if (appType && appType.trim()) {
      uploadDir = appType.toLowerCase()
    }
    
    if (uploadDir) {
      return `${uploadDir}/${timestamp}-${random}.${ext}`
    } else {
      return `${timestamp}-${random}.${ext}`
    }
  }

  /**
   * 上传文件 (最终版：使用 multipartUpload 以确保进度回调的可靠性)
   */
  async uploadFile(file, options = {}) {
    const {
      onProgress,
      // onSuccess and onError are no longer used by TaskModel, but we keep them for compatibility
      onSuccess,
      onError,
      objectName,
      appType = ''
    } = options

    try {
      console.log(`🚀 开始上传流程 (使用 multipartUpload): ${file.name}`)
      await this.initClient()

      const finalObjectName = objectName || this.generateObjectName(file, appType)
      console.log(`📄 生成对象名: ${finalObjectName}`)

      const uploadOptions = {
        // progress 回调在 multipartUpload 中非常可靠
        progress: (p, checkpoint) => {
          const progressPercent = Math.floor(p * 100)
          // 这行日志现在应该会频繁打印了！
          console.log(`🚀 OSS 分片上传进度: ${progressPercent}%`)
          if (onProgress) {
            onProgress({
              isNormal: true,
              value: progressPercent,
              checkpoint
            })
          }
        },
        parallel: 4,
        partSize: 1024 * 1024,
        checkpoint: options.checkpoint || null,
        headers: {
          'x-oss-meta-original-filename': encodeURIComponent(file.name),
          'x-oss-meta-file-size': file.size
        }
      }
      
      console.log(`📤 开始分片上传到OSS: ${finalObjectName}`)
      
      // --- 核心改动：使用 multipartUpload 代替 put ---
      const result = await this.client.multipartUpload(finalObjectName, file, uploadOptions)

      if (result.res.status === 200) {
        console.log(`✅ OSS分片上传成功: ${finalObjectName}`)
        
        // multipartUpload 的返回结果中没有 url，我们需要自己拼接
        console.log('🔧 构建文件URL，client options:', this.client.options)
        console.log('🔧 STS data:', this.stsData)
        
        // 安全地处理配置
        const bucket = this.client.options.bucket || this.stsData.bucket || 'pgxcloud-upload-dev'
        const region = this.client.options.region || this.stsData.region || 'cn-beijing'
        
        // 构建标准的OSS URL格式
        const fileUrl = `https://${bucket}.oss-${region}.aliyuncs.com/${finalObjectName}`
        
        console.log('🔧 构建的文件URL:', fileUrl)

        const uploadResult = {
          url: fileUrl,
          name: finalObjectName,
          size: file.size,
          etag: result.etag,
          // multipartUpload 的结果没有 versionId
        }

        // 为了兼容性，我们仍然可以调用 onSuccess
        if (onSuccess) {
          onSuccess(uploadResult)
        }

        return uploadResult // 返回包含 url 的完整结果
      } else {
        throw new Error(`Multipart upload failed with status: ${result.res.status}`)
      }

    } catch (error) {
      console.error('❌ 分片上传过程中发生错误:', error)
      if (onProgress) {
        onProgress({
          isNormal: true,
          value: 0,
          error: true
        })
      }
      if (onError) {
        onError(error)
      }
      throw error // 继续向上抛出错误，让 TaskModel 能捕获到
    }
  }

  /**
   * 批量上传文件
   */
  async uploadFiles(files, options = {}) {
    const results = []
    const { onProgress, onSuccess, onError } = options

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      try {
        const result = await this.uploadFile(file, {
          ...options,
          onProgress: (progress) => {
            if (onProgress) {
              // 计算总体进度
              const totalProgress = ((i + progress.value / 100) / files.length) * 100
              onProgress({
                isNormal: true,
                value: Math.floor(totalProgress)
              })
            }
          }
        })
        
        results.push(result)
        
        if (onSuccess) {
          onSuccess(result, i)
        }
        
      } catch (error) {
        if (onError) {
          onError(error, i)
        }
        throw error
      }
    }

    return results
  }

  /**
   * 恢复断点续传 (使用 multipartUpload)
   */
  async resumeUpload(file, checkpoint, options = {}) {
    try {
      await this.initClient()
      
      const objectName = options.objectName || this.generateObjectName(file, options.appType || '')
      
      const uploadOptions = {
        ...options,
        checkpoint: checkpoint,
        progress: (p, newCheckpoint) => {
          if (options.onProgress) {
            options.onProgress({
              isNormal: true,
              value: Math.floor(p * 100),
              checkpoint: newCheckpoint
            })
          }
        }
      }

      return await this.client.multipartUpload(objectName, file, uploadOptions)
      
    } catch (error) {
      console.error('Resume upload error:', error)
      throw error
    }
  }

  /**
   * 检查文件是否已存在
   */
  async checkFileExists(objectName) {
    try {
      await this.initClient()
      const result = await this.client.head(objectName)
      return result.res.status === 200
    } catch (error) {
      return false
    }
  }

  /**
   * 删除文件
   */
  async deleteFile(objectName) {
    try {
      await this.initClient()
      const result = await this.client.delete(objectName)
      return result.res.status === 204
    } catch (error) {
      console.error('Delete file error:', error)
      return false
    }
  }
}

// 创建单例实例
export const ossUploader = new OssUploader()

// 导出便捷方法
export const uploadFile = (file, options) => ossUploader.uploadFile(file, options)
export const uploadFiles = (files, options) => ossUploader.uploadFiles(files, options)
export const resumeUpload = (file, checkpoint, options) => ossUploader.resumeUpload(file, checkpoint, options)
export const generateObjectName = (file, subfolder) => ossUploader.generateObjectName(file, subfolder)



// WEBPACK FOOTER //
// ./src/app/components/normal/BasicModal/UploadModal/tools/uploadWithSDK.js